import { Link } from '@tanstack/react-router'
import { CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react'

import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/**
 * Dual control on the administration screens — the same two-part message every other module keeps
 * its own copy of (`groups/dual-control.tsx`, `loans/dual-control.tsx`, `ledger/dual-control.tsx`).
 *
 * It matters more here than almost anywhere else in the application, because administration is the
 * one module where a countersigned mistake widens *access* rather than moves money: a role change
 * or a new account that goes live on one signature is a standing capability, not a single posting,
 * and it sits there until somebody notices.
 *
 * The **control** — that the action is dual-controlled at all — is a fact about the operation,
 * taken from the API contract, and is always stated. The **outcome** is not knowable until the
 * server answers: the approval policy is branch-configurable and may demand a second person even
 * from someone holding both halves of the permission pair, so the notice reports only what the
 * browser does know (whether this user's own role holds the countersigning code) and phrases the
 * rest as a likelihood. {@link AdminDualControlOutcome} renders the definite answer, from
 * `awaitingApproval` on the response.
 */
export function AdminDualControlNotice({
  action,
  approvePermission,
  canCountersign,
}: {
  /** The action, as a noun phrase: "Creating this account". */
  action: string
  approvePermission: string
  canCountersign: boolean
}) {
  return (
    <Alert tone={canCountersign ? 'info' : 'warning'}>
      {canCountersign ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>{action} is countersigned</AlertTitle>
        <AlertDescription>
          {canCountersign ? (
            <>
              Your role holds <span className="font-mono text-xs">{approvePermission}</span>, so
              this may complete as soon as you confirm it. If the branch&apos;s approval policy
              still calls for a second person, it is queued instead and this screen says so
              afterwards.
            </>
          ) : (
            <>
              Your role does not hold <span className="font-mono text-xs">{approvePermission}</span>,
              so confirming this stores the request and changes nothing yet. Someone holding that
              permission has to countersign it before it takes effect — do not treat the account or
              the change as live until then.
            </>
          )}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/**
 * What actually happened.
 *
 * `awaitingApproval: true` means the command is stored and nothing has changed, so the wording is
 * "sent for approval" and never "created" or "saved" — a new account with no approval yet cannot
 * sign in, and a role change that is still queued has not widened anyone's access.
 */
export function AdminDualControlOutcome({
  result,
  awaitingDescription,
  appliedDescription,
}: {
  result: DualControlResult
  /** What is now waiting, in one line. */
  awaitingDescription: string
  /** What has now changed, in one line. */
  appliedDescription: string
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <UserCheck aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for approval — nothing has changed yet</AlertTitle>
          <AlertDescription>
            {awaitingDescription} It takes effect when a second person countersigns it.
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}
          </AlertDescription>
          <Button asChild variant="secondary" size="sm">
            <Link to="/approvals" search={{ tab: 'mine' }}>
              Track it under Submitted by me
            </Link>
          </Button>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Done</AlertTitle>
        <AlertDescription>{appliedDescription}</AlertDescription>
      </div>
    </Alert>
  )
}
